export type PageNotFoundTelemetryPayload = {
  readonly locale: string;
  readonly referrer_bucket: "external" | "internal" | "none";
  readonly route_path: "/missing" | "/missing/missing" | "/missing/deep";
  readonly sampling_tier: "bot_probe" | "standard";
};

const botProbePattern = /(?:^|\/)(?:\.env|\.git|admin|cgi-bin|phpmyadmin|wp-admin|wp-content|wp-login)(?:\/|\.|$)/i;
const botUserAgentPattern = /bot|crawler|spider|slurp|headless|lighthouse/i;

export function buildPageNotFoundTelemetry(
  pathname: string,
  referrer: string,
  origin: string,
  userAgent: string,
  locale: string,
): PageNotFoundTelemetryPayload | null {
  const samplingTier = isBotProbe(pathname, userAgent) ? "bot_probe" : "standard";
  const samplingDenominator = samplingTier === "bot_probe" ? 20 : 4;

  if (stableHash(pathname) % samplingDenominator !== 0) {
    return null;
  }

  return {
    locale: normalizeLocale(locale),
    referrer_bucket: referrerBucket(referrer, origin),
    route_path: structuralMissingPath(pathname),
    sampling_tier: samplingTier,
  };
}

function normalizeLocale(locale: string): string {
  const normalized = locale.trim().toLowerCase();
  return /^[a-z]{2}(?:-[a-z]{2})?$/.test(normalized) ? normalized : "en";
}

function structuralMissingPath(pathname: string): PageNotFoundTelemetryPayload["route_path"] {
  const segments = pathname.split("/").filter(Boolean);

  if (segments[0] && /^[a-z]{2}(?:-[a-z]{2})?$/i.test(segments[0])) {
    segments.shift();
  }

  if (segments.length <= 1) return "/missing";
  if (segments.length === 2) return "/missing/missing";
  return "/missing/deep";
}

function referrerBucket(referrer: string, origin: string): PageNotFoundTelemetryPayload["referrer_bucket"] {
  if (!referrer) return "none";

  try {
    return new URL(referrer, origin).origin === new URL(origin).origin ? "internal" : "external";
  } catch {
    return "none";
  }
}

function isBotProbe(pathname: string, userAgent: string): boolean {
  return botProbePattern.test(pathname) || botUserAgentPattern.test(userAgent);
}

function stableHash(value: string): number {
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}
